import { filterEnabledNotifs, type NotifPrefs } from "@/lib/notificationPrefs";
import type { LiveEvent, LivePayload } from "@/lib/supabase/liveChannel";

/**
 * Logika murni badge lonceng notifikasi PEMBELI — lonceng di header storefront,
 * tepat di kiri ikon keranjang (sebelum 2026-09-24: slot "Notif" di navigasi
 * bawah). Mesin datanya `NotificationProvider` (`src/context/NotificationContext.tsx`),
 * tampilannya `NotificationBell`.
 */

/** Baris minimal untuk badge — kolom yang juga dibawa broadcast `notif:<id>` (mig 4440). */
export type NotifBadgeRow = {
  id: string;
  is_read: boolean;
  type: string;
  important?: boolean | null;
};

/**
 * Jalur tempat mesin lonceng pembeli TIDAK jalan — cermin penjaga `BottomNav`,
 * tempat lonceng dulu terpasang: panel admin & platform memakai scope sesi lain
 * (`createBrowserSupabase` mendeteksi scope dari URL), dan `/toko` = katalog
 * reseller white-label untuk pembeli AKHIR reseller, bukan pelanggan toko.
 */
export function notifBadgeEnabledOnPath(pathname: string): boolean {
  return !(
    pathname.startsWith("/admin") ||
    pathname.startsWith("/toko") ||
    pathname.startsWith("/platform")
  );
}

/**
 * Terapkan satu event broadcast ke daftar belum-dibaca. Dihapus atau sudah
 * dibaca → keluar dari daftar; selain itu upsert ber-dedupe id — broadcast
 * boleh mengirim ulang, dan daftar ini dipakai MENGHITUNG badge: satu duplikat
 * langsung terlihat sebagai angka yang salah. Payload tanpa id diabaikan.
 */
export function applyNotifLiveEvent(
  prev: NotifBadgeRow[],
  event: LiveEvent,
  payload: LivePayload
): NotifBadgeRow[] {
  const id = payload.id;
  if (!id) return prev;
  if (event === "DELETE" || payload.is_read) {
    return prev.filter((row) => row.id !== id);
  }
  const row: NotifBadgeRow = {
    id,
    is_read: false,
    type: payload.type ?? "",
    important: payload.important ?? null,
  };
  return prev.some((existing) => existing.id === id)
    ? prev.map((existing) => (existing.id === id ? row : existing))
    : [...prev, row];
}

/**
 * Angka badge: belum-dibaca yang jenisnya aktif di Pengaturan Notifikasi —
 * notif `important` (broadcast kritis) tetap dihitung walau jenisnya dimatikan.
 * Saringannya `filterEnabledNotifs`, yang sama dengan halaman Notifikasi.
 */
export function unreadBadgeCount(rows: readonly NotifBadgeRow[], prefs: NotifPrefs): number {
  return filterEnabledNotifs(
    rows.filter((row) => !row.is_read),
    prefs
  ).length;
}

/** Teks badge: di atas 99 → "99+" supaya badge tak melebar di header. */
export function formatBadgeCount(count: number): string {
  return count > 99 ? "99+" : String(count);
}
